import React, { useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { updateUserProfile } from "../services/firestoreService";

const ClientAccount: React.FC = () => {
  const { user, userDoc, firmSettings, firmId: authFirmId, refreshFirmSettings } = useAuth();

  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [localPreview, setLocalPreview] = useState<string>("");

  // Prefer AuthContext firmId, fall back to doc fields if present
  const firmId = useMemo(() => {
    return (
      authFirmId ||
      (userDoc as any)?.firmId ||
      (userDoc as any)?.locationId ||
      sessionStorage.getItem("FILERSHUB_SSO_LOC") ||
      null
    );
  }, [authFirmId, userDoc]);

  // Resolve avatar url across possible field names + local preview
  const avatarUrl = useMemo(() => {
    return (
      localPreview ||
      (userDoc as any)?.avatarUrl ||
      (userDoc as any)?.photoUrl ||
      (userDoc as any)?.photoURL ||
      (userDoc as any)?.profilePhotoUrl ||
      ""
    );
  }, [localPreview, userDoc]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!user || !e.target.files || !e.target.files[0]) return;

    if (!firmId) {
      setError("Missing firm context. Please refresh and try again.");
      return;
    }

    const file = e.target.files[0];

    if (!file.type.match(/^image\//)) {
      setError("Please select a valid image file (PNG or JPEG).");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("File size must be less than 5MB.");
      return;
    }

    setIsUploading(true);
    setError("");
    setSuccessMsg("");

    // Instant UI feedback
    const previewUrl = URL.createObjectURL(file);
    setLocalPreview(previewUrl);

    try {
      await updateUserProfile(firmId, user.uid, {}, file);

      setSuccessMsg("Profile photo updated successfully!");

      // Not required, but helps if your UI uses firm settings branding anywhere
      try {
        await refreshFirmSettings?.();
      } catch {}
    } catch (err) {
      console.error(err);
      setLocalPreview("");
      setError("Failed to upload image. Please try again.");
    } finally {
      setIsUploading(false);
      // Allow re-uploading the same file
      e.target.value = "";
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-4 md:p-8">
      <h1 className="text-2xl font-bold text-slate-800 mb-2">My Profile</h1>
      <p className="text-slate-500 mb-8">View your personal information and manage your account photo.</p>

      <div className="space-y-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 flex flex-col sm:flex-row items-center gap-6">
          <div className="relative group">
            <div className="w-24 h-24 rounded-full border-4 border-slate-50 overflow-hidden shadow-sm bg-slate-100 flex items-center justify-center">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={userDoc?.displayName || "Profile Photo"}
                  className="w-full h-full object-cover"
                  onError={() => setLocalPreview("")}
                />
              ) : (
                <span className="text-slate-400 font-black text-sm">No Photo</span>
              )}
            </div>

            <label
              htmlFor="avatar-upload"
              className="absolute bottom-0 right-0 bg-brand-600 text-white p-2 rounded-full cursor-pointer hover:bg-brand-700 shadow-md border-2 border-white transition-transform hover:scale-105"
              title="Upload profile photo"
            >
              {isUploading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                  />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              )}
            </label>

            <input
              type="file"
              id="avatar-upload"
              accept="image/png, image/jpeg"
              className="hidden"
              onChange={handleImageUpload}
              disabled={isUploading}
            />
          </div>

          <div className="text-center sm:text-left">
            <h2 className="text-lg font-bold text-slate-800">Profile Photo</h2>
            <p className="text-sm text-slate-500 mb-2">Upload a clear photo to help your Tax Pro identify you.</p>
            {error && <p className="text-xs text-red-600 font-medium">{error}</p>}
            {successMsg && <p className="text-xs text-emerald-600 font-medium">{successMsg}</p>}
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-slate-800">Personal Details</h2>
            <span className="text-xs bg-slate-100 text-slate-500 px-2 py-1 rounded tracking-widest font-black uppercase">Read-Only</span>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1 tracking-widest">Full Name</label>
              <input
                type="text"
                value={userDoc?.displayName || ""}
                disabled
                className="w-full px-4 py-2 bg-white border border-slate-200 rounded-lg text-slate-600 cursor-not-allowed font-medium"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1 tracking-widest">Email Address</label>
              <input
                type="text"
                value={userDoc?.email || ""}
                disabled
                className="w-full px-4 py-2 bg-white border border-slate-200 rounded-lg text-slate-600 cursor-not-allowed font-medium"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1 tracking-widest">Phone Number</label>
              <input
                type="text"
                value={(userDoc as any)?.phone || "Not provided"}
                disabled
                className="w-full px-4 py-2 bg-white border border-slate-200 rounded-lg text-slate-600 cursor-not-allowed font-medium"
              />
            </div>

            <div className="pt-4 border-t border-slate-100">
              <p className="text-sm text-slate-500 font-medium">Need to update your info? Contact {firmSettings.name}.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientAccount;
